import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { PlanbookEditRepeatingSchedule } from "@shared/models/analytics/planbook"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course } from "@shared/models/course"
import { findCourseDatesOff } from "cc-frontend/lib/dates-off-calculator"
import Patch from "@shared/models/patch-updater"
import _ from "lodash"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultCourseDateActivity from "./default-course-date-activity"
import defaultCourseDateContext from "./default-course-date-context"
import defaultCourseDateModels from "./default-course-date-models"

interface Payload {
  dateStrings: Array<string>
  course: Course
}

export const COURSE_DATE_ANNOTATION_DELETE_MULTIPLE = {
  name: "COURSE_DATE_ANNOTATION_DELETE_MULTIPLE",

  params: {
    dateStrings: "array",
    course: "object",
  },

  patches(payload: Payload) {
    let newAnnotations = _.reject(
      payload.course.attributes.calendar.dateAnnotations,
      (annotation) => {
        return (
          annotation.type === "date-annotation-course-custom-single" &&
          _.includes(payload.dateStrings, annotation.attributes.dateString)
        )
      }
    )
    let allDatesOff = findCourseDatesOff(newAnnotations)
    let patch = Patch.create("course", payload.course.id)
      .set("attributes.calendar.dateAnnotations", newAnnotations)
      .set("attributes.calendar.datesOff", allDatesOff)
      .inc("attributes.calendar.version", 1)

    return [patch.patch]
  },

  undoPatches(payload: Payload) {
    let oldAnnotations = JSON.parse(
      JSON.stringify(payload.course.attributes.calendar.dateAnnotations)
    )
    let allDatesOff = findCourseDatesOff(oldAnnotations)

    let patch = Patch.create("course", payload.course.id)
      .set("attributes.calendar.dateAnnotations", oldAnnotations)
      .set("attributes.calendar.datesOff", allDatesOff)
      .inc("attributes.calendar.version", 1)

    return [patch.patch]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let models = defaultCourseDateModels(payload, findFn, userId)
    let courseTitle = payload.course.attributes.title
    let context = defaultCourseDateContext(payload, models)
    let numberOfDates = payload.dateStrings.length
    let newContext = _.omit(_.assign({}, context, { dates: numberOfDates }), ["date"])
    let newEvent: PlanbookEditRepeatingSchedule = {
      ...createBlankEvent(),
      root_id: models.planbook.id,
      root_type: "planbook",
      primary_object_type: "planbook",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook.id),
      primary_object_id_and_ancestor_ids: [models.planbook.id],
      primary_verb: "edit",
      primary_object_specifier: "repeating_schedule",
      adverbial_verb: "toggling",
      adverbial_object_type: "course",
      adverbial_object_id_and_ancestor_ids: [models.course.id, models.planbook.id],
    }

    return {
      // this is less weird as a separate undo because the choice they made didn't maintain the status quo.
      title: "Turned Multiple Lessons Back On",
      titleForAnalytics: "Lesson - Toggle Multiple On - Schedule Modal",
      description: `Turned ${numberOfDates} lessons back on while editing the schedule for your ${courseTitle} class`,
      context: newContext,
      activity: defaultCourseDateActivity(payload, models, "removed"),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default COURSE_DATE_ANNOTATION_DELETE_MULTIPLE
